import React, { createContext, useContext, useCallback, useRef } from 'react';

interface NotificationRefreshContextType {
  registerRefreshCallback: (callback: () => Promise<void>) => () => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationRefreshContext = createContext<NotificationRefreshContextType | undefined>(undefined);

interface NotificationRefreshProviderProps {
  children: React.ReactNode;
}

export function NotificationRefreshProvider({ children }: NotificationRefreshProviderProps) {
  const refreshCallbacksRef = useRef<Set<() => Promise<void>>>(new Set());

  const registerRefreshCallback = useCallback((callback: () => Promise<void>) => {
    refreshCallbacksRef.current.add(callback);
    
    // Return unregister function
    return () => {
      refreshCallbacksRef.current.delete(callback);
    };
  }, []);

  const refreshNotifications = useCallback(async () => {
    const callbacks = Array.from(refreshCallbacksRef.current);
    
    // Execute all refresh callbacks in parallel
    await Promise.allSettled(callbacks.map(callback => callback()));
  }, []);

  const value = {
    registerRefreshCallback,
    refreshNotifications
  };

  return (
    <NotificationRefreshContext.Provider value={value}>
      {children}
    </NotificationRefreshContext.Provider>
  );
}

export function useNotificationRefresh() {
  const context = useContext(NotificationRefreshContext);
  
  if (context === undefined) {
    throw new Error('useNotificationRefresh must be used within a NotificationRefreshProvider');
  }
  
  return context;
} 